// ── BiName.js — 품목명 한중 병기 공용 헬퍼 (V100) ─────────────────────────
//
// 품목 중국어는 단가표(prices[].name_zh)에만 저장된다.
// 발주서·재고·식재료 화면은 그 단가표를 참조해서 병기만 한다.
// → 중국어를 한 군데서만 관리하면 되고, 데이터 중복이 없다.
//
// 병기는 언어 설정과 무관하게 항상 표시된다. 사장님과 중국 직원이
// 같은 표를 보고 소통해야 하기 때문.

import React from 'react';
import { useLang } from './LangContext';
import { pinyinOf, usePinyinReady, romanizeKo } from './pron';   // V126 발음

/**
 * 단가표에서 한국어명 → 중국어명 맵을 만든다.
 * 공백 무시 키도 같이 넣어서 "다진 마늘"과 "다진마늘"을 같게 본다.
 * @param {Array} prices 단가표
 * @param {Array} [recipes] 레시피 목록 (V122: 단가표에 없는 이름은 레시피 중국어 이름으로)
 * @returns {Map<string,string>}
 */
export function buildZhMap(prices, recipes) {
  const m = new Map();
  for (const p of prices || []) {
    const ko = String(p?.name || '').trim();
    const zh = String(p?.name_zh || '').trim();
    if (!ko || !zh) continue;
    m.set(ko, zh);
    const squished = ko.replace(/\s+/g, '');
    if (!m.has(squished)) m.set(squished, zh);
  }
  // V122: 반제품(레시피)을 식재료로 쓸 때 — 단가표 줄에 중국어가 없으면 레시피의 중국어 이름으로
  for (const r of recipes || []) {
    const ko = String(r?.name || '').trim();
    const zh = String(r?.name_zh || '').trim();
    if (!ko || !zh) continue;
    if (!m.has(ko)) m.set(ko, zh);
    const squished = ko.replace(/\s+/g, '');
    if (!m.has(squished)) m.set(squished, zh);
  }
  return m;
}

/**
 * 맵에서 중국어명을 찾는다. 없으면 빈 문자열.
 */
export function zhOf(zhMap, ko) {
  if (!zhMap || !ko) return '';
  const k = String(ko).trim();
  if (!k) return '';
  return zhMap.get(k) || zhMap.get(k.replace(/\s+/g, '')) || '';
}

/**
 * 한 줄 병기 문자열 — 인쇄 HTML·카카오 공유 텍스트처럼 JSX를 못 쓰는 곳용.
 * 중국어가 없으면 한국어만 그대로 돌려준다.
 */
export function biText(zhMap, ko) {
  const zh = zhOf(zhMap, ko);
  const k = String(ko || '');
  return zh ? `${k} / ${zh}` : k;
}

/**
 * 병기 컴포넌트 — 화면용.
 * 한국어 화면: 한국어 / 중국어(작게) / 병음(더 작게)
 * 중국어 화면: 중국어 / 한국어(작게) / 로마자(더 작게)   ← V126: 보는 언어를 앞에, 한국어 밑에 읽는 법
 * 중국어가 없으면 한국어만 (중국어 화면이면 그 밑에 로마자).
 * pron={false} 이면 발음 줄을 빼고 두 줄만 (규격·단위·업체명처럼 이름이 아닌 칸).
 *
 * <BiName ko={item.name} zhMap={zhMap} />
 * <BiName ko={item.name} zh={item.name_zh} />   // 직접 넘겨도 됨
 */
export default function BiName({ ko, zhMap, zh, size = 11, color = '#1A56A0', style, pron = true }) {
  const { lang } = useLang();
  const z = (zh && String(zh).trim()) || zhOf(zhMap, ko);
  const isZh = lang === 'zh';
  usePinyinReady(pron && !isZh && !!z);   // 병음 사전이 준비되면 다시 그린다

  const second = {
    display: 'block', fontSize: size, fontWeight: 500,
    color, marginTop: 2, lineHeight: 1.3, ...style,
  };
  const third = {
    display: 'block', fontSize: Math.max(9, size - 1), fontWeight: 400,
    color: '#8A8A93', marginTop: 1, lineHeight: 1.25, letterSpacing: '0.01em',
  };

  if (isZh) {
    const rom = pron ? romanizeKo(ko) : '';
    if (!z) return <>{ko}{rom && <span style={third}>{rom}</span>}</>;
    return (
      <>
        {z}
        <span style={second}>{ko}</span>
        {rom && <span style={third}>{rom}</span>}
      </>
    );
  }

  if (!z) return <>{ko}</>;
  const py = pron ? pinyinOf(z) : '';
  return (
    <>
      {ko}
      <span style={second}>{z}</span>
      {py && <span style={third}>{py}</span>}
    </>
  );
}
